import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchState, saveState, EMPTY_STATE } from './bffState';
import type { StudioState } from './bffState';
import { isBffConfigured } from '../../config';
import { storage } from './storage';

const POLL_MS = 30_000;

export interface StudioStateAPI {
  state:           StudioState | null;
  ready:           boolean;
  version:         number;
  isDeleted:       (slug: string) => boolean;
  isArchived:      (slug: string) => boolean;
  /** Désarchivé depuis le catalogue : l'emporte sur meta.status 'archived' du code. */
  isUnarchived:    (slug: string) => boolean;
  getCategory:     (slug: string) => string | null;
  getTitle:        (slug: string) => string | null;
  getDescription:  (slug: string) => string | null;
  getStatus:       (slug: string) => string | null;
  setDeleted:      (slug: string, val: boolean) => Promise<void>;
  setArchived:     (slug: string, val: boolean) => Promise<void>;
  setCategory:     (slug: string, val: string | null) => Promise<void>;
  setTitle:        (slug: string, val: string | null) => Promise<void>;
  setDescription:  (slug: string, val: string | null) => Promise<void>;
  setStatus:       (slug: string, val: string | null) => Promise<void>;
}

function useLocalFallback(): StudioStateAPI {
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((n) => n + 1);
  return {
    state: null, ready: true, version,
    isDeleted:   (slug) => storage.isDeleted(slug),
    isArchived:  (slug) => storage.isArchived(slug),
    isUnarchived: (slug) => storage.isUnarchived(slug),
    getCategory: (slug) => storage.getCategory(slug),
    getTitle:    (slug) => storage.getTitle(slug),
    getDescription: (slug) => storage.getDescription(slug),
    getStatus:   (slug) => storage.getStatus(slug),
    setDeleted:  async (slug, val) => { storage.setDeleted(slug, val); bump(); },
    setArchived: async (slug, val) => { storage.setArchived(slug, val); bump(); },
    setCategory: async (slug, val) => { storage.setCategory(slug, val); bump(); },
    setTitle:    async (slug, val) => { storage.setTitle(slug, val); bump(); },
    setDescription: async (slug, val) => { storage.setDescription(slug, val); bump(); },
    setStatus:   async (slug, val) => { storage.setStatus(slug, val); bump(); },
  };
}

function useBffState(): StudioStateAPI {
  const [state, setState] = useState<StudioState | null>(null);
  const [ready, setReady] = useState(false);
  const [version, setVersion] = useState(0);
  const loadedRef = useRef(false);
  // Source de vérité synchrone : deux mutations enchaînées (ex. archiver puis vider la catégorie)
  // ne doivent pas repartir du même état périmé capturé par le rendu.
  const stateRef = useRef<StudioState | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await fetchState();
      stateRef.current = s;
      setState(s);
      loadedRef.current = true;
      setReady(true);
    } catch {
      loadedRef.current = true; // allow mutations even if fetch failed
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (!isBffConfigured()) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const mutate = useCallback(async (updater: (s: StudioState) => StudioState) => {
    if (!loadedRef.current) return;
    const next = updater(stateRef.current ?? EMPTY_STATE);
    stateRef.current = next;
    setState(next);
    setVersion((n) => n + 1);
    try {
      await saveState(next);
    } catch { /* write errors are non-blocking */ }
  }, []);

  return {
    state, ready, version,
    isDeleted:   (slug) => state?.deleted.includes(slug) ?? false,
    isArchived:  (slug) => state?.archived.includes(slug) ?? false,
    isUnarchived: (slug) => state?.unarchived?.includes(slug) ?? false,
    getCategory: (slug) => state?.categories[slug] ?? null,
    getTitle:    (slug) => state?.titles[slug] ?? null,
    getDescription: (slug) => state?.descriptions?.[slug] ?? null,
    getStatus:   (slug) => state?.statuses?.[slug] ?? null,

    setDeleted: (slug, val) => mutate((s) => ({
      ...s,
      deleted: val ? [...s.deleted.filter((x) => x !== slug), slug] : s.deleted.filter((x) => x !== slug),
    })),

    setArchived: (slug, val) => mutate((s) => ({
      ...s,
      archived: val ? [...s.archived.filter((x) => x !== slug), slug] : s.archived.filter((x) => x !== slug),
      unarchived: val
        ? (s.unarchived ?? []).filter((x) => x !== slug)
        : [...(s.unarchived ?? []).filter((x) => x !== slug), slug],
    })),

    setCategory: (slug, val) => mutate((s) => {
      const cats = { ...s.categories };
      if (val === null) delete cats[slug]; else cats[slug] = val;
      return { ...s, categories: cats };
    }),

    setTitle: (slug, val) => mutate((s) => {
      const titles = { ...s.titles };
      if (!val?.trim()) delete titles[slug]; else titles[slug] = val.trim();
      return { ...s, titles };
    }),

    setDescription: (slug, val) => mutate((s) => {
      const descriptions = { ...(s.descriptions ?? {}) };
      if (val === null) delete descriptions[slug]; else descriptions[slug] = val.trim();
      return { ...s, descriptions };
    }),

    setStatus: (slug, val) => mutate((s) => {
      const statuses = { ...(s.statuses ?? {}) };
      if (val === null) delete statuses[slug]; else statuses[slug] = val;
      return { ...s, statuses };
    }),
  };
}

export function useStudioState(): StudioStateAPI {
  const bff   = useBffState();
  const local = useLocalFallback();
  // Les hooks sont appelés inconditionnellement — on choisit lequel exposer après
  return isBffConfigured() ? bff : local;
}
